import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'components/common/Button';
import { FormState, useForm, resetForm } from 'components/common/form-utils/form-state';
import { validateForm, Validators } from 'components/common/form-utils/validators';
import { Voucher, VoucherCode } from 'utils/Voucher';
import ReactLoading from 'react-loading';
import VoucherEditorTable from './VoucherEditorTable';
import { requests } from 'api-services';

interface VoucherEditorProps {
  voucherCode: VoucherCode
}

export const VoucherEditor = ({ voucherCode }: VoucherEditorProps) => {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [baseVoucher, setBaseVoucher] = useState<Voucher | undefined>(undefined);
  const [submitState, setSubmitState] = useState<{ status: 'error' | 'done' | 'loading', message?: string } | null>(null);

  const form: FormState = useForm({
    code: {
      visibleName: 'Code',
      requiredIf: (val) => val !== undefined,
    },
    discountType: {
      visibleName: 'Discount type',
      requiredIf: (val) => val !== undefined,
    },
    percentageDiscountValue: {
      visibleName: 'Discount value',
      requiredIf: (val) => val !== undefined,
      validations: [Validators.isInteger(), Validators.minValue(1), Validators.maxValue(100)],
    },
    fixedDiscountValue: {
      visibleName: 'Discount value',
      validations: [Validators.isInteger(), Validators.minValue(0)],
    },
    isValid: {
      visibleName: 'Valid',
      type: 'boolean',
      validations: [],
    },
  });

  form.percentageDiscountValue.requiredIf = (val) => {
    return (form.discountType.value === 'Percentage' && val !== undefined);
  };

  form.fixedDiscountValue.requiredIf = (val) => {
    return (form.discountType.value === 'Fixed' && val !== undefined);
  };


  useEffect(() => {
    requests.voucher.get(voucherCode)
      .then((v) => {
        setBaseVoucher(v);
        setState('ready');
      })
      .catch(e => {
        console.warn(`Can't edit voucher: ${e}`);
        setState('error');
      });
  }, []);

  const getVoucher = (): Partial<Voucher> => {
    const voucher: Partial<Voucher> = {};
    if (form.code.value && !form.code.error) voucher.code = form.code.value;
    if (form.discountType.value && !form.discountType.error) {
      if (form.discountType.value === 'Percentage' || form.discountType.value === 'Fixed') {
        voucher.discountType = form.discountType.value;
      }
    }
    if (form.percentageDiscountValue.value && !form.percentageDiscountValue.error) {
      voucher.discountValue = Number.parseInt(form.percentageDiscountValue.value);
    }
    if (form.fixedDiscountValue.value && !form.fixedDiscountValue.error) {
      voucher.discountValue = Number.parseInt(form.fixedDiscountValue.value);
    }
    if (form.isValid.value && !form.isValid.error) voucher.isValid = form.isValid.getBoolean();
    return voucher;
  };

  const applyVoucherEdits = (): Voucher => {
    if (!baseVoucher) throw new Error('baseVoucher not set before applying edits');
    return {
      ...baseVoucher,
      ...getVoucher(),
    };
  };

  const onSubmit = () => {
    // Prevent submission if no edits have been made.
    if (Object.values(form).filter(field => field.value !== undefined).length === 0) {
      setSubmitState({ status: 'error', message: 'No edits have been made' });
      return;
    }

    if (!validateForm(form)) {
      setSubmitState({ status: 'error', message: 'Voucher field modifications have errors.' });
      return;
    }

    setSubmitState({ status: 'loading' });
    const voucher = applyVoucherEdits() as Voucher;
    requests.voucher.put(voucher, baseVoucher?.code as string)
      .catch((e) => setSubmitState({ status: 'error', message: `Request to modify voucher failed: ${e}` }))
      .then((v) => {
        if (!v) return;
        setBaseVoucher(v);
        resetForm(form);
        setSubmitState({ status: 'done' });
      })
      .catch(() => setSubmitState({ status: 'error', message: 'Voucher updated, but error updating locally. Refresh the page.' }));
  };

  return <React.Fragment>
    {state === 'loading' && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
    {state === 'error' && <span className='text-lg my-8'>An error occurred.</span>}
    {state === 'ready' && baseVoucher
      && <div className='my-8'>
        <div role='heading' className='text-2xl pb-4 font-medium'>Edit voucher &quot;{baseVoucher?.code}&quot;</div>
        <div className='max-w-max mx-auto'>
          <VoucherEditorTable baseVoucher={baseVoucher} VoucherEdits={form} />
          {submitState && submitState.status === 'loading'
            && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
          {submitState && submitState.status === 'error'
            && <div className='text-s text-red-600 mb-4'>
              {submitState.message
                ? <>An error occurred updating the voucher: {submitState.message}</>
                : <>An error occurred updating the voucher.</>}
            </div>}
          {submitState && submitState.status === 'done'
            && <div className='text-s text-green-200 mb-4'>Successfully updated voucher.</div>}
          {(!submitState || submitState.status !== 'loading')
            && <div className='flex flex-row w-full place-content-end space-x-1 pr-4'>
              <Button grey id='cancel-button' linkTo='/admin/vouchers'>Cancel</Button>
              <Button danger id='submit-modifications-button' onClick={onSubmit}>Submit Modifications</Button>
            </div>}
        </div>
      </div>}
  </React.Fragment>;
};

const VoucherEditorPage = () => {
  const { code } = useParams<{ code: string }>();
  return <VoucherEditor voucherCode={code} />;
};

export default VoucherEditorPage;
